import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { TodayPlan } from '@/features/planner/TodayPlan'
import { useBeforeDayStart, useToday } from '@/features/settings/useToday'
import { PageHeading } from '@/components/PageHeading'
import { formatTimeOfDay } from '@convex/model/timeOfDay'

/**
 * Whether today is being planned lives in the URL, so a reload in the middle
 * of planning stays in planning, and Back leaves it.
 */
const search = z.object({
  plan: z.boolean().optional(),
})

export const Route = createFileRoute('/')({
  validateSearch: search,
  component: TodayPage,
})

function TodayPage() {
  const { plan } = Route.useSearch()
  const navigate = Route.useNavigate()
  const today = useToday()
  const rollsOverAt = useBeforeDayStart()

  return (
    <div className="py-6 sm:py-8">
      <PageHeading
        title="Today"
        meta={
          today === undefined
            ? undefined
            : rollsOverAt !== null
              ? // Past midnight, before the boundary. The date on screen is
                // deliberately the one that has not finished yet, and naming
                // the hour it ends is the difference between a feature and a
                // glitch.
                `${formatLongDate(today)} · until ${formatTimeOfDay(rollsOverAt)}`
              : formatLongDate(today)
        }
      />

      {/* No capture field here. Today is what today asks of you, and a field
          for writing down something else was the first thing on it. Capture is
          still one press away everywhere — `c`, or the + in the header — which
          is where it belongs rather than occupying the top of one screen. */}
      <div className="mt-6">
        <TodayPlan
          planning={plan === true}
          setPlanning={(planning) => {
            void navigate({ search: planning ? { plan: true } : {} })
          }}
        />
      </div>
    </div>
  )
}

/** "29 Jul · Wednesday", the shape Todoist puts next to the page title. */
function formatLongDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  const day = parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
  const weekday = parsed.toLocaleDateString(undefined, {
    weekday: 'long',
    timeZone: 'UTC',
  })
  return `${day} · ${weekday}`
}
